import { Stack, Switch, Text, Divider, Button, Group } from '@mantine/core';
import { IconBroadcast } from '@tabler/icons-react';
import type { CPUserPermissionViewData, CPUserMediaData } from '@/lib/types/Auth';

interface PermissionControlsProps {
  permissions: CPUserPermissionViewData;
  onChange: (permissions: CPUserPermissionViewData) => void;
  cpuserMedias: CPUserMediaData[];
  onMediasChange: (medias: CPUserMediaData[]) => void;
  onSelectStreams: () => void;
  disabled: boolean;
  isSubaccount: boolean;
  showUploadProfile: boolean;
}

/**
 * Permission toggle controls with coupling rules matching legacy CPUser.component.tsx exactly.
 *
 * Coupling rules:
 * - Broadcaster ON → clears ALL other permissions
 * - Any non-broadcaster toggle ON → forces broadcaster OFF
 * - Manage Billing ON → sets accountNotify, billingRead, billingWrite, billingNotify, roleBroadcaster=false
 * - Manage Content ON → sets serviceRead, serviceWrite, ingest=true, roleBroadcaster=false
 * - Manage Content OFF → clears serviceRead, serviceWrite but NOT ingest (one-way coupling)
 * - Manage Users ON → sets userRead, userWrite, roleBroadcaster=false
 * - Manage Sub-accounts ON → sets subcustomerRead, subcustomerWrite, roleBroadcaster=false
 * - Upload (ingest) ON → sets ingest=true, roleBroadcaster=false
 * - Support ON → sets support=true, roleBroadcaster=false
 */
export function PermissionControls({
  permissions,
  onChange,
  cpuserMedias,
  onMediasChange,
  onSelectStreams,
  disabled,
  isSubaccount,
  showUploadProfile,
}: PermissionControlsProps) {
  const p = permissions;

  function update(changes: Partial<CPUserPermissionViewData>) {
    onChange({ ...p, ...changes });
  }

  function clearAllPermissions(): CPUserPermissionViewData {
    return {
      ...p,
      accountNotify: false,
      billingNotify: false,
      billingRead: false,
      billingWrite: false,
      userRead: false,
      userWrite: false,
      serviceRead: false,
      serviceWrite: false,
      subcustomerRead: false,
      subcustomerWrite: false,
      ingest: false,
      support: false,
    };
  }

  // Broadcaster toggle
  function handleBroadcasterToggle(checked: boolean) {
    if (checked) {
      // Broadcaster ON → clear all other permissions
      const cleared = clearAllPermissions();
      onChange({ ...cleared, roleBroadcaster: true });
    } else {
      // Broadcaster OFF → clear media selections
      update({ roleBroadcaster: false });
      onMediasChange([]);
    }
  }

  // Manage Billing toggle
  function handleBillingToggle(checked: boolean) {
    if (checked) {
      update({
        accountNotify: true,
        billingRead: true,
        billingWrite: true,
        billingNotify: true,
        roleBroadcaster: false,
      });
    } else {
      update({
        accountNotify: false,
        billingRead: false,
        billingWrite: false,
        billingNotify: false,
      });
    }
  }

  // Manage Content toggle
  function handleContentToggle(checked: boolean) {
    if (checked) {
      update({
        serviceRead: true,
        serviceWrite: true,
        ingest: true, // One-way: content ON forces ingest ON
        roleBroadcaster: false,
      });
    } else {
      // One-way: content OFF does NOT clear ingest
      update({
        serviceRead: false,
        serviceWrite: false,
      });
    }
  }

  // Upload (ingest) toggle
  function handleIngestToggle(checked: boolean) {
    if (checked) {
      update({ ingest: true, roleBroadcaster: false });
    } else {
      update({ ingest: false });
    }
  }

  // Manage Users toggle
  function handleUsersToggle(checked: boolean) {
    if (checked) {
      update({ userRead: true, userWrite: true, roleBroadcaster: false });
    } else {
      update({ userRead: false, userWrite: false });
    }
  }

  // Manage Sub-accounts toggle
  function handleSubaccountsToggle(checked: boolean) {
    if (checked) {
      update({ subcustomerRead: true, subcustomerWrite: true, roleBroadcaster: false });
    } else {
      update({ subcustomerRead: false, subcustomerWrite: false });
    }
  }

  // Support toggle
  function handleSupportToggle(checked: boolean) {
    if (checked) {
      update({ support: true, roleBroadcaster: false });
    } else {
      update({ support: false });
    }
  }

  // Derived states for grouped toggles
  const isBilling = p.billingRead && p.billingWrite;
  const isContent = p.serviceRead && p.serviceWrite;
  const isUsers = p.userRead && p.userWrite;
  const isSubcustomers = p.subcustomerRead && p.subcustomerWrite;

  return (
    <Stack gap="md">
      <Text fw={600} size="sm" tt="uppercase" c="dimmed">Access</Text>

      <Switch
        label="Manage Billing"
        description="View and edit payment methods, invoices, and billing notifications"
        checked={isBilling}
        onChange={(e) => handleBillingToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={isBilling ? 'green' : 'gray'}
      />

      <Switch
        label="Manage Content"
        description="View and edit videos, streams, playlists, players, apps, etc."
        checked={isContent}
        onChange={(e) => handleContentToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={isContent ? 'green' : 'gray'}
      />

      <Switch
        label="Upload"
        description="Upload and ingest media files"
        checked={p.ingest}
        onChange={(e) => handleIngestToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={p.ingest ? 'green' : 'gray'}
      />

      <Switch
        label="Manage Users"
        description="Create, edit, and delete team members"
        checked={isUsers}
        onChange={(e) => handleUsersToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={isUsers ? 'green' : 'gray'}
      />

      {!isSubaccount && (
        <Switch
          label="Manage Sub-Accounts"
          description="Create, edit, and manage sub-accounts"
          checked={isSubcustomers}
          onChange={(e) => handleSubaccountsToggle(e.currentTarget.checked)}
          disabled={disabled}
          color={isSubcustomers ? 'green' : 'gray'}
        />
      )}

      <Switch
        label="Support"
        description="Access support tools"
        checked={p.support}
        onChange={(e) => handleSupportToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={p.support ? 'green' : 'gray'}
      />

      <Switch
        label="Broadcaster"
        description="Limit access to assigned streams only"
        checked={p.roleBroadcaster}
        onChange={(e) => handleBroadcasterToggle(e.currentTarget.checked)}
        disabled={disabled}
        color={p.roleBroadcaster ? 'orange' : 'gray'}
      />

      {p.roleBroadcaster && (
        <Group ml="md">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconBroadcast size={14} />}
            onClick={onSelectStreams}
            disabled={disabled}
          >
            Select Streams ({cpuserMedias.length})
          </Button>
        </Group>
      )}

      <Divider />
      <Text fw={600} size="sm" tt="uppercase" c="dimmed">Notifications</Text>

      <Switch
        label="Billing Notifications"
        checked={p.billingNotify}
        onChange={(e) => update({ billingNotify: e.currentTarget.checked })}
        disabled={disabled}
        color={p.billingNotify ? 'green' : 'gray'}
      />

      {/* Account Notifications — disabled for now, may re-enable later
      <Switch
        label="Account Notifications"
        checked={p.accountNotify}
        onChange={(e) => update({ accountNotify: e.currentTarget.checked })}
        disabled={disabled}
        color={p.accountNotify ? 'green' : 'gray'}
      />
      */}
    </Stack>
  );
}
